import React, { MutableRefObject, ReactNode, useEffect, useRef } from 'react';
import { FormikHelpers, useFormik } from 'formik';
import { nanoid } from 'nanoid';
import { ObjectSchema } from 'yup';

import { ValidationError } from './validation-error';

export interface IFormErrors {
  [key: string]: ValidationError;
}

interface IProps {
  initialValues: { [key: string]: any };
  validationSchema: ObjectSchema<{ [key: string]: any }>;
  children: ReactNode;
  onSubmit: (values: any, setErrors?: (errors: IFormErrors) => void) => Promise<void>;
  onDirty?: () => void;
  enableReinitialize?: boolean;
  formikRef?: MutableRefObject<ReturnType<typeof useFormik>>;
}

export const FormContext = React.createContext({
  id: null,
  disabled: false,
  validationSchema: null,
  formik: null as ReturnType<typeof useFormik>,
  values: {} as { [key: string]: any },
});

function FormContextElement({
  initialValues,
  validationSchema,
  onSubmit,
  children,
  onDirty,
  enableReinitialize = false,
  formikRef,
}: IProps) {
  if (!initialValues) return null;

  const id = useRef(nanoid(10));
  const formik = useFormik({
    initialValues,
    validationSchema,
    enableReinitialize,
    onSubmit: (values: any, { setErrors, setSubmitting }: FormikHelpers<any>) => {
      onSubmit(values, setErrors).finally(() => setSubmitting(false));
    },
  });

  if (formikRef) {
    formikRef.current = formik;
  }

  useEffect(() => {
    if (onDirty && formik.dirty) {
      onDirty();
    }
  }, [formik.dirty]);

  return (
    <FormContext.Provider
      value={{ id: id.current, disabled: false, validationSchema, formik, values: formik.values }}
    >
      {children}
    </FormContext.Provider>
  );
}

export default FormContextElement;
